/*
 * Reduces a filter state to what analytics may see: which filters are active and how long the
 * date range is. Filter values (organization, customer and tag ids, search text) are never sent.
 */

export interface FilterDescription {
  filter_keys: string[];
  date_range_days: number | null;
}

const isActive = (value: unknown): boolean => {
  if (value == null || value === false || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  // "ALL" style enum values mean "no filter".
  if (typeof value === 'string' && value.toUpperCase() === 'ALL') return false;
  return true;
};

const toTime = (value: unknown): number | null => {
  if (typeof value !== 'string' || value.length === 0) return null;
  const time = Date.parse(value);
  return Number.isNaN(time) ? null : time;
};

export const describeFilters = <T extends object>(
  filters: T,
  startKey?: keyof T & string,
  endKey?: keyof T & string,
): FilterDescription => {
  const record = filters as Record<string, unknown>;
  const filterKeys = Object.keys(record)
    .filter((key) => isActive(record[key]))
    .sort();

  const start = startKey != null ? toTime(record[startKey]) : null;
  const end = endKey != null ? toTime(record[endKey]) : null;
  const dateRangeDays =
    start != null && end != null && end >= start
      ? Math.round((end - start) / (24 * 60 * 60 * 1000))
      : null;

  return { filter_keys: filterKeys, date_range_days: dateRangeDays };
};
